import { useState } from 'react';
import { cn } from '@/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import CaretDownIcon from '@/assets/icons/leaderboard-filter/caret-down.svg?react';
import { GROUP_OPTIONS } from '@/constants';
import { UserGroup } from '@sportspredict/shared';
import { DATE_LABELS } from './leaderboard-filter.constants';
import { FilterOptionRow } from './leaderboard-filter-option-row';
import {
	LEADERBOARD_FILTER_PILL_ACTIVE,
	LEADERBOARD_FILTER_PILL_BASE,
	LEADERBOARD_FILTER_PILL_INACTIVE,
	LEADERBOARD_FILTER_PILL_PAD_CARET,
} from './leaderboard-filter-pill.styles';

export type LeaderboardOrgScope = 'global' | UserGroup;

export function getOrgScopeLabel(scope: LeaderboardOrgScope): string {
	if (scope === 'global') return DATE_LABELS.global;
	return GROUP_OPTIONS.find((option) => option.value === scope)?.label ?? scope;
}

interface LeaderboardOrgScopePillProps {
	userGroup: UserGroup;
	orgScope: LeaderboardOrgScope;
	onOrgScopeChange: (scope: LeaderboardOrgScope) => void;
	/** Whether the Global/org stage chip is the active board filter. */
	active: boolean;
	disabled?: boolean;
	className?: string;
}

/**
 * Global / org Filter By pill — shown to users who belong to an organization
 * (e.g. NHSSAA). Shared by WC stage tabs and non-WC round-group tabs.
 */
export function LeaderboardOrgScopePill({
	userGroup,
	orgScope,
	onOrgScopeChange,
	active,
	disabled = false,
	className,
}: LeaderboardOrgScopePillProps) {
	const [open, setOpen] = useState(false);
	const label = getOrgScopeLabel(orgScope);

	const handleSelect = (scope: LeaderboardOrgScope) => {
		onOrgScopeChange(scope);
		setOpen(false);
	};

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<button
					type="button"
					disabled={disabled}
					aria-label={label}
					title={label}
					className={cn(
						LEADERBOARD_FILTER_PILL_BASE,
						LEADERBOARD_FILTER_PILL_PAD_CARET,
						disabled
							? cn(LEADERBOARD_FILTER_PILL_INACTIVE, 'opacity-60')
							: active
								? LEADERBOARD_FILTER_PILL_ACTIVE
								: LEADERBOARD_FILTER_PILL_INACTIVE,
						className
					)}
				>
					{label}
					<CaretDownIcon className="size-3 shrink-0" aria-hidden="true" />
				</button>
			</PopoverTrigger>
			<PopoverContent
				align="start"
				sideOffset={8}
				className={cn(
					'w-[224px] overflow-hidden rounded-lg border-[0.5px] border-solid border-[#E5E5E5] bg-white p-0',
					'shadow-[0_16px_32px_rgba(12,12,13,0.1),0_4px_4px_rgba(12,12,13,0.05)]'
				)}
			>
				<div className="flex h-9 w-full items-center border-b-[0.5px] border-solid border-[#E5E5E5] px-3 py-1.5">
					<p className="font-['DM_Sans'] text-[12px] font-semibold leading-[18px] tracking-[0.48px] text-[#5583F6]">
						Filter By
					</p>
				</div>
				<FilterOptionRow
					label={DATE_LABELS.global}
					selected={orgScope === 'global'}
					onClick={() => handleSelect('global')}
					divider
				/>
				<FilterOptionRow
					label={getOrgScopeLabel(userGroup)}
					selected={orgScope === userGroup}
					onClick={() => handleSelect(userGroup)}
				/>
			</PopoverContent>
		</Popover>
	);
}
